/**
 * Utilitaire de stockage compatible multi-navigateur
 * Gère les cas où sessionStorage/localStorage ne sont pas disponibles
 * (Safari mode privé, navigateurs anciens, etc.)
 */

// Mémoire de secours si sessionStorage n'est pas disponible
let memoryStorage = {};

/**
 * Vérifie si sessionStorage est disponible
 */
const isSessionStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Vérifie si localStorage est disponible
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Déterminer quel stockage utiliser
const storageAvailable = isSessionStorageAvailable();
const localStorageAvailable = isLocalStorageAvailable();

/**
 * Récupère une valeur du stockage
 * @param {string} key - Clé de la valeur
 * @returns {string|null} - Valeur ou null
 */
export const getItem = (key) => {
  try {
    if (storageAvailable) {
      return window.sessionStorage.getItem(key);
    } else if (localStorageAvailable) {
      // Fallback sur localStorage si sessionStorage n'est pas disponible
      return window.localStorage.getItem(key);
    } else {
      // Fallback sur mémoire
      return memoryStorage[key] || null;
    }
  } catch (e) {
    console.warn('Storage getItem error:', e);
    return memoryStorage[key] || null;
  }
};

/**
 * Stocke une valeur
 * @param {string} key - Clé
 * @param {string} value - Valeur
 */
export const setItem = (key, value) => {
  try {
    if (storageAvailable) {
      window.sessionStorage.setItem(key, value);
    } else if (localStorageAvailable) {
      window.localStorage.setItem(key, value);
    }
    // Toujours stocker en mémoire comme backup
    memoryStorage[key] = value;
  } catch (e) {
    console.warn('Storage setItem error:', e);
    // Stocker en mémoire si échec
    memoryStorage[key] = value;
  }
};

/**
 * Supprime une valeur
 * @param {string} key - Clé
 */
export const removeItem = (key) => {
  try {
    if (storageAvailable) {
      window.sessionStorage.removeItem(key);
    }
    if (localStorageAvailable) {
      window.localStorage.removeItem(key);
    }
    delete memoryStorage[key];
  } catch (e) {
    console.warn('Storage removeItem error:', e);
    delete memoryStorage[key];
  }
};

/**
 * Vide le stockage
 */
export const clear = () => {
  try {
    if (storageAvailable) {
      window.sessionStorage.clear();
    }
    if (localStorageAvailable) {
      window.localStorage.clear();
    }
    memoryStorage = {};
  } catch (e) {
    console.warn('Storage clear error:', e);
    memoryStorage = {};
  }
};

/**
 * Récupère et parse une valeur JSON
 * @param {string} key - Clé
 * @returns {object|null} - Objet ou null
 */
export const getJSON = (key) => {
  try {
    const value = getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (e) {
    console.warn('Storage getJSON parse error:', e);
    return null;
  }
};

/**
 * Stocke une valeur en JSON
 * @param {string} key - Clé
 * @param {object} value - Objet à stocker
 */
export const setJSON = (key, value) => {
  try {
    setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Storage setJSON stringify error:', e);
  }
};

/**
 * Vérifie si le stockage est disponible
 * @returns {boolean}
 */
export const isAvailable = () => {
  return storageAvailable || localStorageAvailable;
};

/**
 * Retourne le type de stockage utilisé
 * @returns {string}
 */
export const getStorageType = () => {
  if (storageAvailable) return 'sessionStorage';
  if (localStorageAvailable) return 'localStorage';
  return 'memory';
};

export default {
  getItem,
  setItem,
  removeItem,
  clear,
  getJSON,
  setJSON,
  isAvailable,
  getStorageType,
};
